import { useState, useEffect } from 'react';
import {
  Phone, ClipboardCheck, Compass, Building2, Truck, Calendar,
  Mail, MessageSquare, FileText, DollarSign, Briefcase, GraduationCap,
  Shield, Crown, Settings, RefreshCw, Save, ChevronRight, Eye, EyeOff,
  AlertTriangle, TrendingUp, Zap, LayoutGrid, Sparkles, Stamp,
  Bell, Users, BarChart2, ListChecks
} from 'lucide-react';
import { toast } from 'sonner';
import getApiUrl from '../../utils/getApiUrl';

const API = `${getApiUrl()}/api`;
const authFetch = (url, opts = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, { ...opts, credentials: "include", headers: { "Content-Type": "application/json", ...(opts.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
};

const POLES = [
  { id: "telepro", label: "Telepro", icon: Phone, color: "blue" },
  { id: "r1", label: "R1", icon: ClipboardCheck, color: "green" },
  { id: "r2", label: "R2", icon: Compass, color: "cyan" },
  { id: "amo", label: "AMO", icon: Building2, color: "violet" },
  { id: "chantiers", label: "Chantiers", icon: Truck, color: "orange" },
  { id: "calendrier", label: "Calendrier", icon: Calendar, color: "teal" },
  { id: "communications_ia", label: "IA", icon: Mail, color: "sky" },
  { id: "marketing", label: "Marketing", icon: MessageSquare, color: "rose" },
  { id: "administratif", label: "Admin", icon: FileText, color: "slate" },
  { id: "grands_comptes", label: "Comptes", icon: Briefcase, color: "amber" },
  { id: "compta_finances", label: "Compta", icon: DollarSign, color: "emerald" },
  { id: "formations", label: "Formations", icon: GraduationCap, color: "purple" },
  { id: "comite_direction", label: "Direction", icon: Crown, color: "amber" },
  { id: "tests", label: "Tests", icon: Settings, color: "purple" },
];

// Catalogue of available widgets
const WIDGET_CATALOG = [
  {
    id: "sania_banner",
    name: "Banniere Performance SAN-IA",
    description: "Carrousel intelligent avec alertes, motivation, meteo performance et pipeline. Adapte ses messages au contexte.",
    icon: TrendingUp,
    color: "orange",
    category: "Performance",
    defaultSettings: {
      seuils: {
        appels_avant_pause: 50,
        perte_vitesse_pourcentage: 20,
        cq_retard_heures: 48,
      },
      confirmation_rdv: {
        heure_limite: "10:30",
        messages: {
          rdv_demain: "RDV de demain non confirme ! Client: {client} - Contacte-le avant ce soir !",
          rdv_jour_j: "RDV AUJOURD'HUI non confirme ! {client} - Appelle MAINTENANT !",
          rdv_lundi_vendredi: "RDV de lundi non confirme ! Appelle {client} avant ce soir ou lundi avant 10h30",
          rdv_lundi_matin: "RDV AUJOURD'HUI (lundi) non confirme ! {client} - Appelle avant 10h30 !",
        },
      },
      messages: {
        pause_recommandee: ["Tu as passe {appels} appels ! Prends une pause"],
        perte_vitesse: ["Attention, {current} RDV cette semaine contre {last} la semaine derniere"],
        cq_retard: ["{count} controles qualite en retard ! Rattrape-toi vite !"],
      },
      messages_info: {
        telepro_actifs: "{count} telepros actifs. Top : {top} ({top_count} leads).",
        leads_non_assignes: "{count} leads non assignes a un telepro.",
        rdv_avance: "EN AVANCE ! {current} RDV cette semaine vs {last} la semaine derniere.",
        pipeline_stats: "Pipeline : {r1} R1, {amo} transmis AMO.",
        total_pipeline: "{total} fiches dans le pipeline. {rdv_week} RDV cette semaine.",
      },
      messages_motivation: {
        matin: "Bon {jour} matin ! C'est le moment de remplir l'agenda.",
        apres_midi: "Apres-midi productif ! On continue a pousser le pipeline.",
        soir: "Bonne soiree {jour}. Bien joue pour aujourd'hui !",
      },
      fatima_ia: {
        enabled: false,
        tone: "motivant et direct, avec une touche d'humour",
        instructions: "",
        cache_minutes: 15,
        message_count: 6,
      },
    },
  },
  {
    id: "visa_ia",
    name: "Visa IA",
    description: "File d'attente des actions IA en attente de validation humaine. Approuvez ou rejetez les actions directement.",
    icon: Stamp,
    color: "purple",
    category: "IA & Securite",
    defaultSettings: {},
  },
  {
    id: "mes_rappels",
    name: "Mes Rappels",
    description: "Widget de suivi des rappels du jour. Affiche les rappels planifies et permet de les traiter directement.",
    icon: Bell,
    color: "blue",
    category: "Productivite",
    defaultSettings: {},
  },
  {
    id: "communication_interne",
    name: "Communication Interne",
    description: "Messagerie interne entre collaborateurs. Echangez rapidement avec votre equipe sans quitter le dashboard.",
    icon: MessageSquare,
    color: "sky",
    category: "Collaboration",
    defaultSettings: {},
  },
  {
    id: "ma_todo_list",
    name: "Ma Todo List",
    description: "Liste de taches personnelle. Gerez vos actions a faire et suivez votre progression au quotidien.",
    icon: ListChecks,
    color: "emerald",
    category: "Productivite",
    defaultSettings: {},
  },
  {
    id: "filtre_telepros",
    name: "Filtre Telepros",
    description: "Filtrage par teleprospecteur. Permet de visualiser les performances et leads par telepro.",
    icon: Users,
    color: "indigo",
    category: "Filtres",
    defaultSettings: {},
  },
  {
    id: "stats_ringover",
    name: "Stats Ringover",
    description: "Statistiques d'appels Ringover en temps reel. Nombre d'appels, duree moyenne et taux de decroche.",
    icon: BarChart2,
    color: "teal",
    category: "Performance",
    defaultSettings: {},
  },
  {
    id: "banniere_leads_live",
    name: "Banniere Nouveaux Leads",
    description: "Carousel en bas de page affichant les nouveaux leads en temps reel avec bouton d'appel direct.",
    icon: Phone,
    color: "emerald",
    category: "Bannières",
    defaultSettings: {},
  },
  {
    id: "banniere_nouveau_r1",
    name: "Banniere Nouveau R1",
    description: "Carousel en bas de page affichant les nouveaux RDV R1 confirmes avec details client et technicien.",
    icon: Calendar,
    color: "blue",
    category: "Bannières",
    defaultSettings: {},
  },
];

function WidgetSettingsEditor({ widget, settings, onChange }) {
  if (widget.id !== "sania_banner") return null;

  const seuils = settings?.seuils || widget.defaultSettings.seuils;
  const confirmRdv = settings?.confirmation_rdv || widget.defaultSettings.confirmation_rdv;
  const messages = settings?.messages || widget.defaultSettings.messages;
  const messagesInfo = settings?.messages_info || widget.defaultSettings.messages_info;
  const messagesMotivation = settings?.messages_motivation || widget.defaultSettings.messages_motivation;
  const fatimaIa = settings?.fatima_ia || widget.defaultSettings.fatima_ia;

  const updateFatimaIa = (key, value) => {
    onChange({
      ...settings,
      fatima_ia: { ...fatimaIa, [key]: value },
    });
  };

  const updateSeuil = (key, value) => {
    onChange({
      ...settings,
      seuils: { ...seuils, [key]: parseInt(value) || 0 },
    });
  };

  const updateConfirmRdv = (key, value) => {
    onChange({
      ...settings,
      confirmation_rdv: { ...confirmRdv, [key]: value },
    });
  };

  const updateConfirmRdvMessage = (key, value) => {
    onChange({
      ...settings,
      confirmation_rdv: {
        ...confirmRdv,
        messages: { ...confirmRdv.messages, [key]: value },
      },
    });
  };

  const updateMessages = (key, value) => {
    onChange({
      ...settings,
      messages: { ...messages, [key]: value.split("\n").filter(l => l.trim()) },
    });
  };

  const updateInfoMessage = (key, value) => {
    onChange({
      ...settings,
      messages_info: { ...messagesInfo, [key]: value },
    });
  };

  const updateMotivationMessage = (key, value) => {
    onChange({
      ...settings,
      messages_motivation: { ...messagesMotivation, [key]: value },
    });
  };

  return (
    <div className="space-y-5">
      {/* FATIMA IA — Mode Intelligence Artificielle */}
      <div className={`rounded-xl border p-5 space-y-4 transition-all ${
        fatimaIa.enabled
          ? "bg-gradient-to-r from-pink-500/15 to-violet-500/15 border-pink-500/40"
          : "bg-slate-800/30 border-slate-700/30"
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
              fatimaIa.enabled
                ? "bg-gradient-to-br from-pink-500 to-violet-500"
                : "bg-slate-700"
            }`}>
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className={`font-semibold text-sm ${fatimaIa.enabled ? "text-pink-300" : "text-slate-400"}`}>
                Fatima IA
              </h3>
              <p className="text-[10px] text-slate-500">
                {fatimaIa.enabled
                  ? "Fatima genere les messages du ticker avec l'IA"
                  : "Desactive — les templates statiques sont utilises"}
              </p>
            </div>
          </div>
          <button
            data-testid="fatima-ia-toggle"
            onClick={() => updateFatimaIa("enabled", !fatimaIa.enabled)}
            className={`relative w-12 h-6 rounded-full transition-all ${
              fatimaIa.enabled ? "bg-pink-500" : "bg-slate-700"
            }`}
          >
            <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${
              fatimaIa.enabled ? "left-6" : "left-0.5"
            }`} />
          </button>
        </div>

        {fatimaIa.enabled && (
          <div className="space-y-4 pt-2">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Ton et style de Fatima</label>
              <input
                type="text"
                data-testid="fatima-tone-input"
                value={fatimaIa.tone || ""}
                onChange={(e) => updateFatimaIa("tone", e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-xs"
                placeholder="Ex: motivant et direct, avec une touche d'humour"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Instructions specifiques</label>
              <textarea
                data-testid="fatima-instructions-input"
                value={fatimaIa.instructions || ""}
                onChange={(e) => updateFatimaIa("instructions", e.target.value)}
                rows={3}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-xs"
                placeholder="Ex: Felicite les telepros par leur prenom. Alerte si un telepro n'a pas fait de RDV depuis 2 jours. Mets l'accent sur les objectifs hebdo."
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Cache (minutes)</label>
                <input
                  type="number"
                  data-testid="fatima-cache-input"
                  value={fatimaIa.cache_minutes ?? 15}
                  onChange={(e) => updateFatimaIa("cache_minutes", e.target.value === "" ? "" : parseInt(e.target.value) || "")}
                  onBlur={(e) => updateFatimaIa("cache_minutes", parseInt(e.target.value) || 15)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                  min={5}
                  max={60}
                />
                <p className="text-[10px] text-slate-500 mt-1">Min: 5 min.</p>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Nombre de messages</label>
                <input
                  type="number"
                  data-testid="fatima-message-count-input"
                  value={fatimaIa.message_count ?? 6}
                  onChange={(e) => updateFatimaIa("message_count", e.target.value === "" ? "" : parseInt(e.target.value) || "")}
                  onBlur={(e) => updateFatimaIa("message_count", parseInt(e.target.value) || 6)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                  min={3}
                  max={20}
                />
                <p className="text-[10px] text-slate-500 mt-1">Messages par cycle (3-20).</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Collapse template sections when Fatima is active */}
      {!fatimaIa.enabled && (
        <p className="text-[10px] text-center text-slate-600 -mb-2">
          Mode Templates — Les sections ci-dessous configurent les messages statiques du ticker
        </p>
      )}
      {fatimaIa.enabled && (
        <p className="text-[10px] text-center text-slate-600 -mb-2">
          Les sections ci-dessous servent de fallback si l'IA est indisponible
        </p>
      )}

      {/* Seuils d'alertes */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
        <h3 className="font-semibold text-orange-400 flex items-center gap-2 text-sm">
          <AlertTriangle className="w-4 h-4" />
          Seuils d'Alertes
        </h3>
        <p className="text-xs text-slate-500">Ces seuils declenchent des messages dans la banniere.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Appels avant pause</label>
            <input
              type="number"
              value={seuils.appels_avant_pause}
              onChange={(e) => updateSeuil("appels_avant_pause", e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Perte vitesse (%)</label>
            <input
              type="number"
              value={seuils.perte_vitesse_pourcentage}
              onChange={(e) => updateSeuil("perte_vitesse_pourcentage", e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">CQ retard apres (h)</label>
            <input
              type="number"
              value={seuils.cq_retard_heures}
              onChange={(e) => updateSeuil("cq_retard_heures", e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
            />
          </div>
        </div>
      </div>

      {/* Confirmation RDV */}
      <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl border border-blue-500/30 p-5 space-y-4">
        <h3 className="font-semibold text-blue-400 flex items-center gap-2 text-sm">
          <Calendar className="w-4 h-4" />
          Confirmation RDV (R1)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Heure limite Jour J</label>
            <input
              type="time"
              value={confirmRdv.heure_limite}
              onChange={(e) => updateConfirmRdv("heure_limite", e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
            />
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3 text-xs space-y-1">
            <span className="text-blue-400 font-medium">Regles :</span>
            <p className="text-slate-400"><span className="text-green-400">&#10003;</span> Mar-Ven : Veille ou Jour J avant {confirmRdv.heure_limite}</p>
            <p className="text-slate-400"><span className="text-green-400">&#10003;</span> Lundi : Vendredi ou Lundi avant {confirmRdv.heure_limite}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { key: "rdv_demain", label: "RDV demain non confirme" },
            { key: "rdv_jour_j", label: "RDV Jour J (urgent)" },
            { key: "rdv_lundi_vendredi", label: "Lundi (alerte vendredi)" },
            { key: "rdv_lundi_matin", label: "Lundi matin (urgent)" },
          ].map(({ key, label }) => (
            <div key={key}>
              <label className="block text-xs text-slate-400 mb-1">{label}</label>
              <input
                type="text"
                value={confirmRdv.messages?.[key] || ""}
                onChange={(e) => updateConfirmRdvMessage(key, e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-xs"
                placeholder="{client} = nom du client"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Messages generaux */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-3">
        <h4 className="font-medium text-slate-300 text-sm">Messages Alertes</h4>
        <p className="text-[10px] text-slate-500">Un message par ligne. Variables : {"{appels}"}, {"{current}"}, {"{last}"}, {"{count}"}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { key: "pause_recommandee", label: "Pause recommandee" },
            { key: "perte_vitesse", label: "Perte de vitesse" },
            { key: "cq_retard", label: "CQ en retard" },
          ].map(({ key, label }) => (
            <div key={key}>
              <label className="block text-xs text-slate-400 mb-1">{label}</label>
              <textarea
                value={(messages?.[key] || []).join("\n")}
                onChange={(e) => updateMessages(key, e.target.value)}
                rows={3}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-xs"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Messages Informatifs (ticker) */}
      <div className="bg-gradient-to-r from-purple-500/10 to-indigo-500/10 rounded-xl border border-purple-500/30 p-5 space-y-4">
        <h3 className="font-semibold text-purple-400 flex items-center gap-2 text-sm">
          <TrendingUp className="w-4 h-4" />
          Messages Informatifs (Ticker)
        </h3>
        <p className="text-[10px] text-slate-500">Ces messages s'affichent dans le carrousel du header. Variables disponibles dans chaque champ.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { key: "telepro_actifs", label: "Telepros actifs", placeholder: "{count}, {top}, {top_count}" },
            { key: "leads_non_assignes", label: "Leads non assignes", placeholder: "{count}" },
            { key: "rdv_avance", label: "RDV en avance", placeholder: "{current}, {last}" },
            { key: "pipeline_stats", label: "Stats pipeline", placeholder: "{r1}, {amo}" },
            { key: "total_pipeline", label: "Total pipeline", placeholder: "{total}, {rdv_week}" },
          ].map(({ key, label, placeholder }) => (
            <div key={key}>
              <label className="block text-xs text-slate-400 mb-1">{label}</label>
              <input
                type="text"
                value={messagesInfo?.[key] || ""}
                onChange={(e) => updateInfoMessage(key, e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-xs"
                placeholder={placeholder}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Messages Motivation */}
      <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl border border-green-500/30 p-5 space-y-4">
        <h3 className="font-semibold text-green-400 flex items-center gap-2 text-sm">
          <Zap className="w-4 h-4" />
          Messages Motivation
        </h3>
        <p className="text-[10px] text-slate-500">Messages affiches selon l'heure. Variable : {"{jour}"} = jour de la semaine.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { key: "matin", label: "Matin (8h-12h)" },
            { key: "apres_midi", label: "Apres-midi (14h-18h)" },
            { key: "soir", label: "Soir (18h+)" },
          ].map(({ key, label }) => (
            <div key={key}>
              <label className="block text-xs text-slate-400 mb-1">{label}</label>
              <input
                type="text"
                value={messagesMotivation?.[key] || ""}
                onChange={(e) => updateMotivationMessage(key, e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-xs"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function WidgetConfigPanel() {
  const [config, setConfig] = useState({ widgets: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [expandedWidget, setExpandedWidget] = useState(null);

  // Load config
  useEffect(() => {
    setLoading(true);
    authFetch(`${API}/config/widgets`)
      .then(r => r.json())
      .then(data => {
        setConfig(data || { widgets: {} });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Save config
  const handleSave = async () => {
    setSaving(true);
    try {
      const resp = await authFetch(`${API}/config/widgets`, {
        method: "PUT",
        body: JSON.stringify(config),
      });
      if (resp.ok) {
        toast.success("Configuration widgets sauvegardee !");
      } else {
        toast.error("Erreur lors de la sauvegarde");
      }
    } catch {
      toast.error("Erreur de connexion");
    }
    setSaving(false);
  };

  // Toggle widget active for a pole
  const toggleWidgetForPole = (widgetId, poleId) => {
    setConfig(prev => {
      const widgets = { ...prev.widgets };
      if (!widgets[widgetId]) {
        const catalogWidget = WIDGET_CATALOG.find(w => w.id === widgetId);
        widgets[widgetId] = {
          settings: catalogWidget?.defaultSettings || {},
          poles: {},
        };
      }
      const poles = { ...widgets[widgetId].poles };
      if (!poles[poleId]) {
        poles[poleId] = { enabled: true, position: "top" };
      } else {
        poles[poleId] = { ...poles[poleId], enabled: !poles[poleId].enabled };
      }
      widgets[widgetId] = { ...widgets[widgetId], poles };
      return { ...prev, widgets };
    });
  };

  // Update widget global settings
  const updateWidgetSettings = (widgetId, newSettings) => {
    setConfig(prev => {
      const widgets = { ...prev.widgets };
      if (!widgets[widgetId]) {
        widgets[widgetId] = { settings: {}, poles: {} };
      }
      widgets[widgetId] = { ...widgets[widgetId], settings: newSettings };
      return { ...prev, widgets };
    });
  };

  const getWidgetConfig = (widgetId) => config.widgets?.[widgetId] || {};
  const getActivePoleCount = (widgetId) => {
    const wc = getWidgetConfig(widgetId);
    return Object.values(wc.poles || {}).filter(p => p.enabled).length;
  };

  return (
    <div className="space-y-6" data-testid="widget-config-panel">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 rounded-2xl border border-orange-500/30 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Configuration Widgets</h2>
              <p className="text-sm text-slate-400">Activez et parametrez les widgets par pole</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-widgets-config"
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-medium transition-all disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? "Sauvegarde..." : "Sauvegarder"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-400" />
        </div>
      ) : (
        <div className="space-y-4">
          {WIDGET_CATALOG.map((widget) => {
            const Icon = widget.icon;
            const wConfig = getWidgetConfig(widget.id);
            const activeCount = getActivePoleCount(widget.id);
            const isExpanded = expandedWidget === widget.id;

            return (
              <div
                key={widget.id}
                data-testid={`widget-card-${widget.id}`}
                className="bg-slate-800/30 rounded-xl border border-slate-700/50 overflow-hidden"
              >
                {/* Widget header */}
                <button
                  onClick={() => setExpandedWidget(isExpanded ? null : widget.id)}
                  className="w-full flex items-center gap-4 p-5 hover:bg-slate-800/50 transition-all text-left"
                >
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br from-${widget.color}-500 to-${widget.color}-600 flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <h3 className="text-sm font-bold text-white">{widget.name}</h3>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-700 text-slate-300">
                        {activeCount} pole{activeCount !== 1 ? 's' : ''} actif{activeCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 truncate">{widget.description}</p>
                  </div>
                  <ChevronRight className={`w-5 h-5 text-slate-500 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                </button>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="border-t border-slate-700/50 p-5 space-y-5">
                    {/* Poles activation grid */}
                    <div>
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                        <LayoutGrid className="w-3 h-3 inline mr-1.5" />
                        Poles actifs
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {POLES.map((pole) => {
                          const PoleIcon = pole.icon;
                          const isActive = wConfig.poles?.[pole.id]?.enabled;
                          return (
                            <button
                              key={pole.id}
                              data-testid={`widget-pole-${widget.id}-${pole.id}`}
                              onClick={() => toggleWidgetForPole(widget.id, pole.id)}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                                isActive
                                  ? `bg-${pole.color}-500/20 text-${pole.color}-300 border-${pole.color}-500/40`
                                  : "bg-slate-900/50 text-slate-500 border-slate-700/30 hover:border-slate-600"
                              }`}
                            >
                              {isActive ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                              <PoleIcon className="w-3 h-3" />
                              {pole.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Widget settings */}
                    <div>
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                        <Settings className="w-3 h-3 inline mr-1.5" />
                        Parametres du widget
                      </h4>
                      <WidgetSettingsEditor
                        widget={widget}
                        settings={wConfig.settings || widget.defaultSettings}
                        onChange={(newSettings) => updateWidgetSettings(widget.id, newSettings)}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
